import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EyeIcon } from "lucide-react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { Tooltip, TooltipTrigger } from "@/components/tooltip";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";
import { useIsImpersonated } from "@/utils/isImpersonated";

interface LeaveImpersonateResponse {
  message: string;
  token: string;
}

function useLeaveImpersonate() {
  const queryClient = useQueryClient();
  const { data: userData } = useUser();
  const leaveImpersonationTo = userData?.user.leaveImpersonationTo;

  return useMutation<LeaveImpersonateResponse, Error, void>({
    mutationFn: async () =>
      await apiClient
        .url("/auth/impersonate/leave")
        .post()
        .json<LeaveImpersonateResponse>(),
    onError: (error) => {
      console.error("Error leaving impersonation:", error);
      window.location.reload();
    },
    onSuccess: () => {
      queryClient.clear();
      window.location.href = leaveImpersonationTo ?? "/requests";
    },
  });
}

export function LeaveImpersonate() {
  const leaveImpersonateMutation = useLeaveImpersonate();
  const isUserImpersonated = useIsImpersonated();

  if (!isUserImpersonated) {
    return null;
  }

  const handleLeaveImpersonate = () => {
    leaveImpersonateMutation.mutate();
  };

  return (
    <TooltipTrigger>
      <Button
        className="text-nav-item-button-icon-fg"
        data-cy="impersonateButton"
        isDisabled={leaveImpersonateMutation.isPending}
        onPress={handleLeaveImpersonate}
        size="sm"
        variant="plain"
      >
        <AccessibleIcon>
          <EyeIcon className="transition-colors duration-200 group-hover:text-utility-warning-600" />
        </AccessibleIcon>
      </Button>
      <Tooltip>
        {leaveImpersonateMutation.isPending
          ? "Leaving impersonation..."
          : "Stop impersonating user"}
      </Tooltip>
    </TooltipTrigger>
  );
}
